import { Box, Combobox, InputBase, Stack, useCombobox } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { setSelectedPortfolios } from '../../../../store/process-analysis-store/process-analysis-store.actions';
import { baseBlack, baseWhite, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Checkbox } from '../../../../ui-library/checkbox/checkbox';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import classes from '../../../../ui-library/select/select.module.css';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { usePlaceholderText, usePortfolioOptions } from './main-section.hooks';

const MAX_COMPARISON_PORTFOLIOS = 3;

export const PortfolioComparisonSelector = () => {
  const globalSelectedPortfolios = useProcessAnalysisStore((state) => state.selectedPortfolios);
  const [localSelectedPortfolios, setLocalSelectedPortfolios] = useState<string[]>([]);

  const portfolioOptions = usePortfolioOptions();
  const placeholderText = usePlaceholderText();

  useEffect(() => {
    setLocalSelectedPortfolios(globalSelectedPortfolios);
  }, [globalSelectedPortfolios]);

  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const handlePortfolioToggle = (portfolioId: string) => {
    setLocalSelectedPortfolios((current) => {
      if (current.includes(portfolioId)) {
        return current.filter((id) => id !== portfolioId);
      } else if (current.length < MAX_COMPARISON_PORTFOLIOS) {
        return [...current, portfolioId];
      }
      return current;
    });
  };

  const handleCompare = () => {
    setSelectedPortfolios(localSelectedPortfolios);
    combobox.closeDropdown();
  };

  const options = portfolioOptions.map((option) => {
    const isChecked = localSelectedPortfolios.includes(option.value);
    const disabled =
      !localSelectedPortfolios.includes(option.value) && localSelectedPortfolios.length >= MAX_COMPARISON_PORTFOLIOS;

    return (
      <Tooltip
        key={option.value}
        label={t('common.tooltips.max_comparison_limit', {
          count: MAX_COMPARISON_PORTFOLIOS + 1, // +1 because global portfolio is always included
        })}
        variant="white"
        disabled={!disabled}
        position="right"
      >
        <Combobox.Option value={option.value} key={option.value} p={0}>
          <Checkbox
            label={option.label}
            checked={isChecked}
            disabled={disabled}
            onChange={() => handlePortfolioToggle(option.value)}
            size="sm"
          />
        </Combobox.Option>
      </Tooltip>
    );
  });

  return (
    <Combobox store={combobox} position="bottom-start" offset={{ mainAxis: 4 }}>
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          radius="xl"
          onClick={() => combobox.toggleDropdown()}
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          pointer
          classNames={classes}
          styles={{
            input: {
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '12px',
              color: localSelectedPortfolios.length > 0 ? baseBlack : skyDark,
              cursor: 'pointer',
              textAlign: 'left',
              width: 180,
              height: 30,
              minHeight: 30,
              fontFamily: 'Figtree',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            },
          }}
        >
          {placeholderText}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown p={16} style={{ borderRadius: 8 }}>
        <Stack gap={16}>
          {options.length > 0 ? (
            <Combobox.Options
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                padding: 0,
              }}
            >
              {options}
            </Combobox.Options>
          ) : (
            <Box style={{ textAlign: 'center' }}>
              <Text size="small" color={skyDark}>
                {t('process_analysis.main_section.no_portfolios_available')}
              </Text>
            </Box>
          )}
          <Divider />
          <Button
            variant="secondary"
            radius="xl"
            size="xs"
            leftSection={
              <Icon
                name={localSelectedPortfolios.length === 0 ? 'check' : 'compare_arrows'}
                color={baseWhite}
                size={14}
                pointer
              />
            }
            fullWidth
            onClick={handleCompare}
          >
            {localSelectedPortfolios.length === 0 ? t('common.actions.apply') : t('common.actions.compare')}
          </Button>
        </Stack>
      </Combobox.Dropdown>
    </Combobox>
  );
};
